// All Tools menu — slides in from the LEFT as a side nav panel (matches the
// NavDrawer pattern), a vertical scrollable list of every tool.
import React, { useEffect, useRef, useState } from 'react';
import { Animated, Modal, Pressable, ScrollView, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { ALL_TOOL_IDS, TOOL_NAMES } from '../domain/olmps';
import { useTheme, makeThemedStyles, DISPLAY_FONT, radii } from '../v10/theme';
import { iconForTool } from './toolIcons';
import { GlassFill } from './glass';

const DRAWER_WIDTH_RATIO = 0.78;

export function ToolMenu({
  open,
  onClose,
  onSelectTool,
}: {
  open: boolean;
  onClose: () => void;
  onSelectTool: (toolId: string) => void;
}) {
  const { width } = useWindowDimensions();
  const { t } = useTheme();
  const styles = useStyles();
  const drawerWidth = Math.min(320, width * DRAWER_WIDTH_RATIO);
  const translateX = useRef(new Animated.Value(-drawerWidth)).current;
  const backdropOpacity = useRef(new Animated.Value(0)).current;
  // Keep the Modal mounted through the close animation so the panel slides
  // out (instead of popping) before it unmounts.
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    if (open) setRendered(true);
    Animated.parallel([
      Animated.timing(translateX, {
        toValue: open ? 0 : -drawerWidth,
        duration: 260,
        useNativeDriver: true,
      }),
      Animated.timing(backdropOpacity, {
        toValue: open ? 1 : 0,
        duration: 260,
        useNativeDriver: true,
      }),
    ]).start(({ finished }) => {
      if (finished && !open) setRendered(false);
    });
  }, [open, drawerWidth, translateX, backdropOpacity]);

  return (
    <Modal visible={rendered} transparent animationType="none" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <Animated.View style={[StyleSheet.absoluteFill, styles.scrim, { opacity: backdropOpacity }]}>
          <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        </Animated.View>
        <Animated.View style={[styles.drawer, { width: drawerWidth, transform: [{ translateX }] }]}>
          <GlassFill strong radius={radii.panel} />
          <ScrollView contentContainerStyle={styles.content}>
            <Text style={styles.title}>All Tools</Text>
            {ALL_TOOL_IDS.map((id) => {
              const Icon = iconForTool(id);
              return (
                <Pressable key={id} style={styles.row} onPress={() => onSelectTool(id)}>
                  <Icon size={20} color={t.ivorySoft} strokeWidth={2} />
                  <Text style={styles.rowText}>{TOOL_NAMES[id]}</Text>
                </Pressable>
              );
            })}
          </ScrollView>
        </Animated.View>
      </View>
    </Modal>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
    backdrop: { flex: 1, flexDirection: 'row', justifyContent: 'flex-start' },
    scrim: { backgroundColor: c.overlay },
    drawer: {
      height: '100%',
      backgroundColor: 'transparent',
      overflow: 'hidden',
      borderTopRightRadius: radii.panel,
      borderBottomRightRadius: radii.panel,
      borderRightWidth: 1,
      borderColor: t.navyLine,
      paddingTop: 60,
      ...neu.raisedLg,
    },
    content: { paddingHorizontal: 18, paddingBottom: 40 },
    title: {
      fontSize: 13,
      fontWeight: '800',
      letterSpacing: 2.5,
      color: t.ivoryFaint,
      marginBottom: 12,
      textTransform: 'uppercase',
      fontFamily: DISPLAY_FONT,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 14,
      paddingVertical: 13,
    },
    rowText: { fontSize: 16, fontWeight: '700', color: t.ivory },
  }),
);
